import React, { memo, useEffect } from 'react';
import { useRouter } from 'expo-router';

import { OrderTypeValues } from '@sg/shared-be-features-ordering';

import { useGiftCardPollingMachine } from '@order/features-gift-card';
import {
  type PaymentMethod,
  useOrderPaymentStateFromLocalStorage,
  clearLocalStorageOrderId,
} from '@order/features-ordering';
import { getStripeKeyPrefix } from '@order/integrations-stripe';

import {
  useGiftCardCheckoutFormState,
  type GiftCardCheckoutFormFieldRules,
  type GiftCardCheckoutFormReturn,
} from '../../form';
import { useGiftCardCancelPayment, useGiftCardPaymentState } from '../../hooks';
import { GiftCardCheckoutFormContent, GiftCardCheckoutFormPolling } from './components';
import { GiftCardCheckoutPaymentProvider } from './sections';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardCheckoutForm = memo((props: GiftCardCheckoutFormProps) => {
  const { form } = props;

  return (
    <GiftCardCheckoutPaymentProvider form={form}>
      <GiftCardCheckoutFormReady {...props} />
    </GiftCardCheckoutPaymentProvider>
  );
});

const GiftCardCheckoutFormReady = memo((props: GiftCardCheckoutFormProps) => {
  const {
    sectionTitles,
    form,
    rules,
    submitForm,
    paymentMethods,
    purchaseAttempt,
    incrementPurchaseAttempt,
  } = props;

  // ─── Form State ────────────────────────────────────────────────────────────

  const { amount, isSubmitting, canSubmit, requestFormSubmission } = useGiftCardCheckoutFormState({
    form,
    submitForm,
  });

  // ─── Polling Machine ───────────────────────────────────────────────────────

  const { orderId, orderType } = useOrderPaymentStateFromLocalStorage();
  const hasStoredPendingPayment = orderId && orderType === OrderTypeValues.GiftCardOrder;

  const { fetchGiftCardPaymentState } = useGiftCardPaymentState();
  const { cancelGiftCardPayment } = useGiftCardCancelPayment();

  const {
    orderId: fulfilledOrderId,
    isPaymentPending,
    isPaymentDelayed,
    isCancelingPayment,
    isPaymentFulfilled,
    startPolling,
    resetPolling,
    createPayment,
    clearPaymentState,
    failPayment,
    requestPaymentCancelation,
  } = useGiftCardPollingMachine({
    fetchGiftCardPaymentState,
    cancelGiftCardPayment,
    clearLocalStorageOrderId,
    incrementPurchaseAttempt,
  });

  // ─── Effects ───────────────────────────────────────────────────────────────

  const router = useRouter();

  useEffect(() => {
    if (!hasStoredPendingPayment) return;
    startPolling(orderId);

    return () => {
      resetPolling();
    };
  }, [orderId, hasStoredPendingPayment, startPolling, resetPolling]);

  useEffect(() => {
    if (!isPaymentFulfilled) return;
    router.navigate(`/account/gift/confirmation/${fulfilledOrderId}`);
  }, [fulfilledOrderId, isPaymentFulfilled, router]);

  // ───────────────────────────────────────────────────────────────────────────

  if (isPaymentPending) {
    return (
      <GiftCardCheckoutFormPolling
        isDelayed={isPaymentDelayed}
        isLoading={isCancelingPayment}
        cancelPayment={requestPaymentCancelation}
      />
    );
  }

  return (
    <GiftCardCheckoutFormContent
      key={getStripeKeyPrefix(purchaseAttempt)}
      amount={amount}
      sectionTitles={sectionTitles}
      paymentMethods={paymentMethods}
      form={form}
      rules={rules}
      canSubmit={canSubmit}
      isSubmitting={isSubmitting}
      createPayment={createPayment}
      cancelPayment={requestPaymentCancelation}
      clearPaymentState={clearPaymentState}
      failPayment={failPayment}
      requestFormSubmission={requestFormSubmission}
      startPolling={startPolling}
    />
  );
});

// ─── Types ───────────────────────────────────────────────────────────────────

type SectionTitles = {
  amount: string;
  details: string;
  deliveryDate: string;
  deliveryMethod: string;
  recipientEmail: string;
  paymentMethod: string;
};

type GiftCardCheckoutFormProps = {
  purchaseAttempt: number;
  sectionTitles?: SectionTitles;
  form: GiftCardCheckoutFormReturn;
  rules: GiftCardCheckoutFormFieldRules;
  paymentMethods: readonly PaymentMethod[];
  submitForm: () => Promise<void>;
  incrementPurchaseAttempt: () => void;
};
